import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Surface } from "@vd/ui";
import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  accountKeys,
  billingAccount,
  type PaymentMethodView,
  paymentMethodsQuery,
} from "../../../api/billingAccount";
import { billingKeys, billingSummaryQuery } from "../../../api/billingPlan";
import { errorMessage } from "../../../api/client";
import { requireAdmin } from "../../../auth/useMe";
import {
  type AddedMethod,
  AddPaymentMethodDialog,
} from "../../../components/billing/account/AddPaymentMethodDialog";
import { PaymentMethodRow } from "../../../components/billing/account/PaymentMethodRow";
import { RemoveMethodDialog } from "../../../components/billing/account/RemoveMethodDialog";
import { BillingHeaderAction } from "../../../components/billing/HeaderSlot";
import { toast } from "../../../components/toast";
import { Button, ErrorState, Loading } from "../../../components/ui";

// Admin > Billing > Payment methods (DESIGN.md "Billing (console)", Payment methods tab, story
// P20). The list is Kite's mirror of Stripe, kept by webhooks: a new method, a new default or a
// removal shows here once Stripe reports it, so after each the tab polls for a short while and
// shows the requested state in the meantime.

export const Route = createFileRoute("/admin/billing/payment-methods")({
  staticData: { crumb: { label: "Payment methods", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(paymentMethodsQuery),
  component: PaymentMethodsTab,
});

/** How long the tab polls for the mirror to catch up after a change. */
const SETTLE_MS = 30_000;

function PaymentMethodsTab() {
  const qc = useQueryClient();
  const summary = useQuery(billingSummaryQuery);
  const [settleUntil, setSettleUntil] = useState(0);
  const list = useQuery({
    ...paymentMethodsQuery,
    refetchInterval: () => (Date.now() < settleUntil ? 2000 : false),
  });
  // Requested and not yet in the mirror.
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());
  const [defaulting, setDefaulting] = useState<string | null>(null);
  const [awaiting, setAwaiting] = useState<AddedMethod | null>(null);
  const [adding, setAdding] = useState(false);
  const [toRemove, setToRemove] = useState<PaymentMethodView | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const moreRefs = useRef(new Map<string, HTMLButtonElement>());
  const removeFocus = useRef<HTMLElement | null>(null);

  const settle = () => setSettleUntil(Date.now() + SETTLE_MS);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: accountKeys.methods });
    void qc.invalidateQueries({ queryKey: billingKeys.summary });
  };

  const makeDefault = useMutation({
    mutationFn: (id: string) => billingAccount.makeDefault(id),
    onMutate: (id) => {
      setDefaulting(id);
      settle();
    },
    onSuccess: refresh,
    onError: (e) => {
      setDefaulting(null);
      toast(errorMessage(e), "danger");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => billingAccount.remove(id),
    onSuccess: (_, id) => {
      setRemoving((s) => new Set(s).add(id));
      settle();
      setToRemove(null);
      refresh();
    },
    onError: (e) => {
      setToRemove(null);
      toast(errorMessage(e), "danger");
    },
  });

  const methods = list.data?.methods;
  const setDefault = makeDefault.mutate;

  // Clear the requested states once the mirror shows them.
  useEffect(() => {
    if (!methods) return;
    const ids = new Set(methods.map((m) => m.id));
    setRemoving((s) => {
      const left = [...s].filter((id) => ids.has(id));
      return left.length === s.size ? s : new Set(left);
    });
    setDefaulting((d) => (d && methods.some((m) => m.id === d && m.is_default) ? null : d));
  }, [methods]);

  // A method just added: once it is in the mirror, make it the default when asked to.
  useEffect(() => {
    if (!methods || !awaiting) return;
    if (!awaiting.id) {
      setAwaiting(null);
      return;
    }
    const pm = methods.find((m) => m.id === awaiting.id);
    if (!pm) return;
    setAwaiting(null);
    if (awaiting.makeDefault && pm.state === "ready" && !pm.is_default) setDefault(pm.id);
  }, [methods, awaiting, setDefault]);

  const shown = (methods ?? []).filter((m) => !removing.has(m.id));
  const defaultId = defaulting ?? shown.find((m) => m.is_default)?.id ?? null;
  const hasDefault = shown.some((m) => m.is_default && m.state === "ready");

  return (
    <>
      <BillingHeaderAction>
        <Button ref={addRef} variant="primary" onClick={() => setAdding(true)}>
          <Plus aria-hidden />
          Add payment method
        </Button>
      </BillingHeaderAction>
      <div className="flex max-w-[880px] flex-col gap-5">
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : (
          <Surface asChild radius="xl" className="flex flex-col">
            <section aria-label="Payment methods">
              <ul className="divide-y divide-hairline">
                {shown.length === 0 ? (
                  <li className="flex min-h-14 items-center px-4 text-ink-3 text-ui">No payment methods</li>
                ) : (
                  shown.map((pm) => (
                    <PaymentMethodRow
                      key={pm.id}
                      pm={pm}
                      isDefault={pm.id === defaultId}
                      busy={defaulting === pm.id || (remove.isPending && remove.variables === pm.id)}
                      onMakeDefault={() => makeDefault.mutate(pm.id)}
                      onRemove={() => {
                        removeFocus.current = moreRefs.current.get(pm.id) ?? null;
                        setToRemove(pm);
                      }}
                      moreRef={(el) => {
                        if (el) moreRefs.current.set(pm.id, el);
                        else moreRefs.current.delete(pm.id);
                      }}
                    />
                  ))
                )}
              </ul>
            </section>
          </Surface>
        )}
      </div>
      <AddPaymentMethodDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(m) => {
          setAwaiting(m);
          settle();
          refresh();
        }}
        interval={summary.data?.interval ?? null}
        hasDefault={hasDefault}
        returnFocus={addRef}
      />
      <RemoveMethodDialog
        pm={toRemove}
        busy={remove.isPending}
        onConfirm={() => toRemove && remove.mutate(toRemove.id)}
        onClose={() => setToRemove(null)}
        returnFocus={removeFocus}
      />
    </>
  );
}
